import React from 'react';
import { IRichText } from './utils';
import usePageBlock from 'hooks/use-page-block';
import BlockSettings from './BlockSettings';
import useResizeObserver from 'hooks/use-resize-observer';
import { useSelector } from 'react-redux';
import { isEditMode } from 'services/admin';
import useToolbarPosition from './use-toolbar-position';
import { Image, StyledQuickEditOverlay, StyledFullEditor, ToolbarPositionRef } from './styles';
import { PREDEFINED_TOOLBAR_BUTTONS } from 'components/ui/RichTextEditorV2/utils';

export interface IRichTextItem {
  arrayId: string;
  data: IRichText;
  kind: string;
}

interface IRichTextBlockProps {
  index: number;
  item: IRichTextItem;
  onEdit: (item: IRichTextItem) => void;
  onRemove: () => void;
  /**
   * This is needed for localization. The `pageId` is used
   * in conjunction to the block's `arrayId` to uniquely identify it.
   */
  pageId: string;
}

type EditableStringPath = 'contentRaw';

const RichTextBlock: React.FC<IRichTextBlockProps> = ({
  item,
  onEdit,
  onRemove,
  pageId,
}) => {
  const [ready, setReady] = React.useState(false);
  const isEditing = useSelector(isEditMode);
  const [imageRef, { height }] = useResizeObserver<HTMLImageElement>();
  const { ref, sticking, toolbarHeight } = useToolbarPosition();

  const handleChangeData = React.useCallback((update: IRichText) => {
    onEdit({ ...item, data: update });
  }, [item, onEdit]);

  const { handleChange, handleCloseSettings, handleOpenSettings, isSettingsOpen } = usePageBlock<EditableStringPath, IRichText>({
    item: item.data,
    onChange: handleChangeData,
  });

  const onReady = () => {
    setReady(true);
  };

  return (
    <StyledQuickEditOverlay
      backgroundColor={item.data.background.color}
      headerColor={item.data.header.color}
      imageHeight={item.data.coverImage ? height : 0}
      onRemove={onRemove}
      titleKey="RICH_TEXT"
      onEdit={handleOpenSettings}
      testId={`rich-text-${item.arrayId}`}
      ready={ready}
    >
      {item.data.coverImage && <Image ref={imageRef} src={item.data.coverImage} />}
      <ToolbarPositionRef isEditMode={isEditing} ref={ref}>
        <StyledFullEditor
          inline={false}
          toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.fullEditor}
          data={item.data.contentRaw}
          isEditMode={isEditing}
          onChange={handleChange('contentRaw')}
          sticking={sticking}
          toolbarHeight={toolbarHeight}
          onReady={onReady}
        />
      </ToolbarPositionRef>
      <BlockSettings
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        item={item.data}
        onChange={handleChangeData}
      />
    </StyledQuickEditOverlay>
  );
};

export default RichTextBlock;
